import {
  MapPin,
  Bed,
  Bath,
  Maximize2,
  ArrowRight,
  SquareParking,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Listing } from "@/lib/types";

interface PropertyCardProps {
  property: Listing;
}

export function PropertyCard({ property }: PropertyCardProps) {
  // Currency formatter
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: property.currency || "LKR",
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Get main image URL
  const getMainImage = () => {
    // Assuming images are directly on the property object or handled differently now
    // If property.images is an array of ListingImage objects, take the first one's URL
    if (property.images && property.images.length > 0) {
      const primaryImage =
        property.images.find((img) => img.is_primary) || property.images[0];
      if (primaryImage && primaryImage.url) {
        return primaryImage.url;
      }
    }

    // Fallback to placeholder
    return "/placeholder-property.avif";
  };

  return (
    <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 overflow-hidden group flex flex-col">
      {/* Property Image */}
      <div className="relative h-48 overflow-hidden">
        <Link to={`/property/${property.id}`}>
          <img
            src={getMainImage()}
            alt={property.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = "/placeholder-property.avif";
            }}
          />
        </Link>

        {/* Featured badge */}
        {property.is_featured === true && (
          <Badge className="absolute top-2 right-2 bg-amber-500/90 backdrop-blur-sm hover:bg-amber-600">
            Featured
          </Badge>
        )}

        {/* Listing type badge */}
        <Badge className="absolute top-2 left-2 bg-propertylk-blue/90 backdrop-blur-sm hover:bg-propertylk-darkblue">
          {property.listing_type}
          {property.propertyType?.display_name && (
            <span className="ml-1">({property.propertyType.display_name})</span>
          )}
        </Badge>
      </div>

      {/* Property Info */}
      <div className="p-4 flex-grow">
        {/* Price */}
        <div className="font-bold text-xl text-propertylk-blue mb-2">
          {formatCurrency(property.price)}
          {property.listing_type === "rent" && (
            <span className="text-sm font-normal text-gray-500"> /month</span>
          )}
        </div>

        {/* Title */}
        <Link to={`/property/${property.id}`}>
          <h3 className="text-lg font-medium text-gray-800 mb-2 hover:text-propertylk-blue transition-colors line-clamp-2 min-h-[3rem]">
            {property.title}
          </h3>
        </Link>

        {/* Location */}
        <div className="flex items-center text-gray-600 mb-4">
          <MapPin className="w-4 h-4 mr-1 flex-shrink-0 text-gray-400" />
          <span className="truncate text-sm">
            {property.address || property.city_name || "Location unavailable"}
          </span>
        </div>

        {/* Property Details & Attributes Section */}
        <div className="border-t border-gray-100 pt-4 mt-4 text-sm min-h-[4rem] flex flex-col justify-between">
          {(property.bedrooms ||
            property.bedrooms === 0 ||
            property.bathrooms ||
            property.bathrooms === 0 ||
            property.parking_spaces ||
            property.parking_spaces === 0 ||
            property.size) && (
            <div className="flex items-center gap-4 text-gray-500">
              {property.bedrooms !== null &&
                property.bedrooms !== "NULL" &&
                property.bedrooms !== undefined && (
                  <div className="flex items-center">
                    <Bed className="w-4 h-4 mr-1" />
                    <span>{property.bedrooms}</span>
                  </div>
                )}

              {property.bathrooms !== null &&
                property.bathrooms !== "NULL" &&
                property.bathrooms !== undefined && (
                  <div className="flex items-center">
                    <Bath className="w-4 h-4 mr-1" />
                    <span>{property.bathrooms}</span>
                  </div>
                )}

              {property.parking_spaces !== null &&
                property.parking_spaces !== "NULL" &&
                property.parking_spaces !== undefined && (
                  <div className="flex items-center">
                    <SquareParking className="w-4 h-4 mr-1" />
                    <span>{property.parking_spaces}</span>
                  </div>
                )}

              {property.size !== null && property.size !== "NULL" && (
                <div className="flex items-center">
                  <Maximize2 className="w-4 h-4 mr-1" />
                  <span>
                    {property.size} {property.size_unit || "sqft"}
                  </span>
                </div>
              )}
            </div>
          )}

          {property.attributes && property.attributes.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {property.attributes.slice(0, 3).map((attribute) => (
                <Badge
                  key={attribute.id}
                  variant="secondary"
                  className="text-xs"
                >
                  {attribute.name}
                </Badge>
              ))}
              {property.attributes.length > 3 && (
                <Badge variant="secondary" className="text-xs">
                  +{property.attributes.length - 3} more
                </Badge>
              )}
            </div>
          )}
        </div>
      </div>

      {/* View Link */}
      <div className="px-4 pb-4 mt-2">
        <Link
          to={`/property/${property.id}`}
          className="inline-flex items-center text-propertylk-blue hover:text-propertylk-darkblue transition-colors group w-full"
        >
          <span className="font-medium border-b border-transparent group-hover:border-current">
            View Details
          </span>
          <ArrowRight className="ml-1 h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
    </div>
  );
}

// Add default export to fix import issues
export default PropertyCard;
